import React, { useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { runGrammarAnalysis } from '../../services/grammarAnalysis';
import { downloadPdfReport, downloadTextReport, highlightSegments } from '../../services/analysisReport';

const { FiX, FiDownload, FiFileText, FiAlertCircle, FiLoader } = FiIcons;

const CATEGORY_LABELS = {
  spelling: 'Possible spelling issue',
  grammar: 'Possible grammar issue',
  punctuation: 'Possible punctuation issue',
  style: 'Style suggestion',
  clarity: 'Clarity suggestion',
  general: 'Contextual suggestion',
};

const estimateReviewRange = (wordCount) => {
  const low = Math.max(1, Math.ceil(wordCount / 40000));
  const high = Math.max(low + 1, Math.ceil(wordCount / 20000));
  return { low, high };
};

const formatElapsed = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return minutes > 0
    ? `${minutes}m ${String(remainingSeconds).padStart(2, '0')}s`
    : `${remainingSeconds}s`;
};

const GrammarAnalysisDrawer = ({
  manuscriptId,
  manuscriptFile,
  manuscriptContent,
  manuscriptTitle,
  onClose,
}) => {
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [content, setContent] = useState('');
  const [matches, setMatches] = useState([]);
  const [wordCount, setWordCount] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    let active = true;
    setStatus('loading');
    setError('');
    setWordCount(0);

    const timer = window.setInterval(() => {
      if (active) setElapsedSeconds(seconds => seconds + 1);
    }, 1000);

    runGrammarAnalysis({
      manuscriptId,
      manuscriptFile,
      manuscriptContent,
      onContentReady: text => {
        if (active) {
          const words = text.trim().split(/\s+/).filter(Boolean).length;
          setWordCount(words);
        }
      },
    })
      .then(result => {
        if (!active) return;
        setContent(result.manuscriptContent);
        setMatches(result.matches);
        setStatus('ready');
      })
      .catch(problem => {
        if (!active) return;
        setError(problem.message || 'Analysis could not be completed.');
        setStatus('error');
      });

    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [manuscriptId, manuscriptFile, manuscriptContent]);

  const segments = highlightSegments(content, matches);
  const estimate = wordCount ? estimateReviewRange(wordCount) : null;
  const exportPdf = () => downloadPdfReport(matches, manuscriptTitle).catch(problem => setError(problem.message));
  const exportTxt = () => downloadTextReport(matches, manuscriptTitle);

  return (
    <div className="fixed inset-0 z-[90] flex justify-end" role="dialog" aria-modal="true" aria-label="Grammar and spelling suggestions">
      <button type="button" className="absolute inset-0 bg-slate-900/50" onClick={onClose} aria-label="Close analysis drawer" />
      <section className="relative flex h-full w-full max-w-5xl flex-col bg-white shadow-2xl">
        <header className="flex items-start justify-between border-b border-slate-200 px-6 py-5">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-primary">Editorial review</p>
            <h2 className="mt-1 text-xl font-serif font-bold text-slate-800">Possible spelling and grammar issues</h2>
            <p className="mt-1 text-sm text-slate-500">Suggestions are prompts to review, not automatic corrections.</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label="Close">
            <SafeIcon icon={FiX} />
          </button>
        </header>

        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-6 py-3">
          <p className="text-sm font-semibold text-slate-600">
            {status === 'ready' ? `${matches.length} possible ${matches.length === 1 ? 'issue' : 'issues'} flagged` : 'Checking manuscript…'}
          </p>
          {status === 'ready' && (
            <div className="flex gap-2">
              <button type="button" onClick={exportTxt} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs font-bold text-slate-600 hover:bg-slate-50">
                <SafeIcon icon={FiFileText} /> Download TXT
              </button>
              <button type="button" onClick={exportPdf} className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-xs font-bold text-white hover:bg-blue-800">
                <SafeIcon icon={FiDownload} /> Download PDF
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="mx-6 mt-4 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
            <SafeIcon icon={FiAlertCircle} className="mt-0.5 shrink-0" /> {error}
          </div>
        )}
        {status === 'loading' && (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center text-slate-600">
            <SafeIcon icon={FiLoader} className="animate-spin text-xl text-primary" />
            <p className="text-base font-semibold">Please sit tight while we review your manuscript.</p>
            <p className="max-w-lg text-sm leading-relaxed text-slate-500">
              {estimate
                ? `This manuscript is about ${wordCount.toLocaleString()} words. The review may take around ${estimate.low}–${estimate.high} minutes, depending on server load. Please keep this window open while suggestions are prepared.`
                : 'We are loading your manuscript and preparing the review. Larger manuscripts may take a minute or two, depending on server load.'}
            </p>
            <p className="text-xs font-medium text-slate-400">Time elapsed: {formatElapsed(elapsedSeconds)}</p>
          </div>
        )}

        {status === 'ready' && (
          <div className="grid min-h-0 flex-1 grid-cols-1 divide-y divide-slate-200 lg:grid-cols-2 lg:divide-x lg:divide-y-0">
            <div className="min-h-0 overflow-y-auto p-6">
              <h3 className="mb-3 text-xs font-bold uppercase tracking-widest text-slate-500">Manuscript text</h3>
              <pre className="whitespace-pre-wrap break-words font-serif text-sm leading-7 text-slate-700">
                {segments.map((segment, index) => segment.match ? (
                  <mark
                    key={`${segment.match.id}-${index}`}
                    id={`grammar-match-${segment.match.id}`}
                    title={`${CATEGORY_LABELS[segment.match.category]}: ${segment.match.message}`}
                    className="rounded bg-amber-200 px-0.5 text-slate-900"
                  >
                    {segment.text}
                  </mark>
                ) : <React.Fragment key={`text-${index}`}>{segment.text}</React.Fragment>)}
              </pre>
            </div>
            <div className="min-h-0 overflow-y-auto bg-slate-50 p-6">
              <h3 className="mb-3 text-xs font-bold uppercase tracking-widest text-slate-500">Contextual suggestions</h3>
              {matches.length === 0 ? (
                <p className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">No suggestions were returned. This is not a guarantee that the manuscript is error-free.</p>
              ) : (
                <ul className="space-y-3">
                  {matches.map(match => (
                    <li key={match.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <span className="rounded-full bg-amber-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-amber-800">{CATEGORY_LABELS[match.category]}</span>
                        {match.start >= 0 && (
                          <button type="button" onClick={() => document.getElementById(`grammar-match-${match.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })} className="text-xs font-bold text-primary hover:underline">
                            View highlight
                          </button>
                        )}
                      </div>
                      <p className="text-sm font-medium leading-relaxed text-slate-700">{match.message}</p>
                      {match.text && <p className="mt-2 text-xs text-slate-500">Matched: “{match.text}”</p>}
                      {match.suggestion && <p className="mt-1 text-xs text-slate-500">Possible suggestion: {match.suggestion}</p>}
                      {!match.text && <p className="mt-2 text-xs text-slate-400">The checker did not provide a matched text range for highlighting.</p>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default GrammarAnalysisDrawer;